/**
 * Booking panel — Calendar / Gantt / Market Validation tabs.
 *
 * EF-5 (final implementation): three independent views inside a single
 * #booking panel, switched via the existing .tab-btn pattern.
 *
 * - Calendar: Year → Quarter → Month → Week → Day drill-down with a
 *   persistent project table below that filters on the selected cell.
 * - Gantt: SVG horizontal timeline with Division → Brand swimlanes,
 *   one bar per project (start = OCD, end = delivery date).
 * - Market Validation: brand grid → click brand → project picker →
 *   select N → validate popin with per-country dates + quantity +
 *   owner + notes → submit creates forecast entries.
 */

import { getState, subscribe } from "../state";
import { escapeHtml } from "../utils/escapeHtml";
import { deadlineBadgeHtml } from "../components/DeadlineBadge";
import { formatNumber } from "../utils/formatting";
import { onPanelActivate, getActivePanel } from "../router";
import { getProvider } from "../providerInstance";
import { showToast } from "../components/Toast";
import { exportCalendarPdf, exportCsv } from "../services/export";
import { triggerRefresh } from "../refreshService";
import { renderEmptyState } from "../components/EmptyState";
import { openValidationDrawer, closeValidationDrawer, defaultChildDate, type ValidationCountry } from "../components/ValidationDrawer";
import { getAccessRules, allowedCountries as filterAllowedCountries } from "./accessRules";
import { renderProgressBar } from "../components/ProgressBar";
import {
  normalizeValue,
  unique,
} from "./shared";

// ── Tab state ────────────────────────────────────────────────

type BookingTab = "calendar" | "gantt" | "validation";
let _activeTab: BookingTab = "calendar";

export function getActiveBookingTab(): BookingTab {
  return _activeTab;
}

// ── Calendar drill-down state ────────────────────────────────

type CalLevel = "year" | "quarter" | "month" | "week" | "day";

interface CalSelection {
  level: CalLevel;
  year: number;
  quarter?: number; // 1..4
  month?: number; // 1..12
  week?: number; // ISO week 1..53
  day?: string; // YYYY-MM-DD
}

let _calSelection: CalSelection = { level: "year", year: new Date().getUTCFullYear() };
let _calTypeFilter = "";

// ── Validation table state ──────────────────────────────────

let _valPage = 1;
const _valPageSize = 50;
let _valSortKey: keyof ValidationProject | "brand" | "division" | "type" | "parentDeliveryDate" =
  "projectName";
let _valSortAsc = true;
let _valSelectedBrand = "";
const _valSelectedIds = new Set<string>();

// ── Init ─────────────────────────────────────────────────────

export function init(): void {
  onPanelActivate("booking", render);
  subscribe("assets", render);
  subscribe("forecasts", render);
  subscribe("rules", render);
  bindTabs();
  bindCalendarHandlers();
  bindGanttHandlers();
  bindValidationHandlers();
}

// ── Render dispatcher ────────────────────────────────────────

export function render(): void {
  if (getActivePanel() !== "booking") return;
  if (_activeTab === "calendar") renderCalendarView();
  else if (_activeTab === "gantt") renderGanttView();
  else if (_activeTab === "validation") renderValidationView();
}

// ── Tab wiring ───────────────────────────────────────────────

function bindTabs(): void {
  document.addEventListener("click", (event) => {
    const btn = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-booking-tab]",
    );
    if (!btn) return;
    const next = btn.dataset.bookingTab as BookingTab | undefined;
    if (!next || next === _activeTab) return;
    _activeTab = next;
    document.querySelectorAll<HTMLElement>("[data-booking-tab]").forEach((b) => {
      b.classList.toggle("is-active", b === btn);
    });
    document.getElementById("booking-calendar-view")?.classList.toggle("hidden", next !== "calendar");
    document.getElementById("booking-gantt-view")?.classList.toggle("hidden", next !== "gantt");
    document.getElementById("booking-validation-view")?.classList.toggle("hidden", next !== "validation");
    render();
  });
}

// ── Helpers — date / asset normalisation ─────────────────────

interface NormalizedProject {
  id: string;
  projectName: string;
  brand: string;
  division: string;
  hub: string;
  type: string;
  ocdDate: string; // YYYY-MM-DD or empty
  deliveryDate: string; // YYYY-MM-DD or empty
  assetCount: number;
  forecastTotal: number;
  parents: any[];
  children: any[];
}

export interface ValidationProject {
  id: string; // `${projectName}::${brand}`
  projectName: string;
  brand: string;
  division: string;
  type: string;
  parentDeliveryDate: string;        // earliest parent requestedDeliveryDate
  assetCount: number;
  totalVolume: number;                // sum assetVolume over all children
  countries: string[];                // sorted unique hubs in children
  childrenByCountry: Map<string, any[]>;
  volumeByCountry: Map<string, number>;
  deliveryByCountry: Map<string, string>;
  validatedCountries: Set<string>;    // hubs with at least one matching forecast
}

export function buildValidationProjects(): ValidationProject[] {
  const state = getState();

  const validatedAssetIds = new Set<string>();
  for (const f of state.forecasts as any[]) {
    if (f.assetId) validatedAssetIds.add(String(f.assetId));
  }

  const map = new Map<string, ValidationProject>();
  for (const a of state.assets as any[]) {
    const projectName = String(a.projectName || a.name || "Unknown").trim();
    const brand = String(a.brand || "Unknown").trim();
    const id = `${projectName}::${brand}`;
    let row = map.get(id);
    if (!row) {
      row = {
        id,
        projectName,
        brand,
        division: String(a.division || ""),
        type: String(a.type || ""),
        parentDeliveryDate: "",
        assetCount: 0,
        totalVolume: 0,
        countries: [],
        childrenByCountry: new Map(),
        volumeByCountry: new Map(),
        deliveryByCountry: new Map(),
        validatedCountries: new Set(),
      };
      map.set(id, row);
    }
    const hub = String(a.hub || "").trim();
    const isParent = normalizeValue(a.parentChild) === "parent";
    const volume = Number(a.assetVolume || 0);

    row.assetCount += 1;
    row.totalVolume += volume;

    if (isParent) {
      const pd = isoFromAny(a.requestedDeliveryDate || a.launchDate);
      if (pd && (!row.parentDeliveryDate || pd < row.parentDeliveryDate)) {
        row.parentDeliveryDate = pd;
      }
      // Parents usually carry no hub-specific data; skip the per-country maps.
      continue;
    }

    if (!hub) continue; // a child with no hub cannot be validated per-country

    if (!row.childrenByCountry.has(hub)) row.childrenByCountry.set(hub, []);
    row.childrenByCountry.get(hub)!.push(a);
    row.volumeByCountry.set(hub, (row.volumeByCountry.get(hub) || 0) + volume);

    const dd = isoFromAny(a.requestedDeliveryDate || a.launchDate);
    if (dd) {
      const current = row.deliveryByCountry.get(hub);
      if (!current || dd < current) row.deliveryByCountry.set(hub, dd);
    }

    if (validatedAssetIds.has(String(a.id))) row.validatedCountries.add(hub);
  }

  for (const row of map.values()) {
    row.countries = [...row.childrenByCountry.keys()].sort((a, b) =>
      a.localeCompare(b),
    );
  }

  return [...map.values()].sort((a, b) =>
    a.projectName.localeCompare(b.projectName),
  );
}

/**
 * Group raw assets into one logical project per (projectName + brand + hub).
 * Each group aggregates the parents and children, picks the earliest OCD as
 * "start" and the latest deliveryDate as "end". Counts assets and totals the
 * forecast quantity from the filtered forecasts list.
 */
function buildProjects(): NormalizedProject[] {
  const state = getState();
  const groups = new Map<string, NormalizedProject>();

  // Index forecasts by assetId for the totals
  const fcByAsset = new Map<string, number>();
  for (const f of state.forecasts as any[]) {
    const id = String(f.assetId || f.id || "");
    if (!id) continue;
    fcByAsset.set(id, (fcByAsset.get(id) || 0) + Number(f.quantity || 0));
  }

  for (const a of state.assets as any[]) {
    const projectName = String(a.projectName || a.name || "Unknown").trim();
    const brand = String(a.brand || "Unknown").trim();
    const hub = String(a.hub || "—").trim();
    const key = `${projectName}::${brand}::${hub}`;
    let row = groups.get(key);
    if (!row) {
      row = {
        id: key,
        projectName,
        brand,
        division: String(a.division || ""),
        hub,
        type: String(a.type || ""),
        ocdDate: "",
        deliveryDate: "",
        assetCount: 0,
        forecastTotal: 0,
        parents: [],
        children: [],
      };
      groups.set(key, row);
    }
    row.assetCount += 1;
    row.forecastTotal += fcByAsset.get(String(a.id)) || 0;
    if (normalizeValue(a.parentChild) === "parent") row.parents.push(a);
    else row.children.push(a);

    const ocd = isoFromAny((a as any).ocdDate || (a as any).briefDate || (a as any).dmiMadDate);
    if (ocd && (!row.ocdDate || ocd < row.ocdDate)) row.ocdDate = ocd;

    const dd = isoFromAny((a as any).requestedDeliveryDate || (a as any).launchDate);
    if (dd && (!row.deliveryDate || dd > row.deliveryDate)) row.deliveryDate = dd;
  }

  return [...groups.values()].sort((a, b) =>
    a.projectName.localeCompare(b.projectName),
  );
}

/** Normalize a date string of any format to YYYY-MM-DD, or "" if unparseable. */
export function isoFromAny(input: any): string {
  if (!input) return "";
  const s = String(input).trim();
  if (!s) return "";
  // already ISO
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // DD/MM/YYYY
  m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  // MM/DD/YYYY (US) — fallback if first attempt fails
  return "";
}

// ── ISO week helpers (year, week 1..53, day-of-week) ─────────

export function isoWeekOf(dateIso: string): { year: number; week: number } {
  const [y, mo, d] = dateIso.split("-").map(Number);
  const date = new Date(Date.UTC(y, mo - 1, d));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const firstThursdayDayNum = (firstThursday.getUTCDay() + 6) % 7;
  firstThursday.setUTCDate(firstThursday.getUTCDate() - firstThursdayDayNum + 3);
  const week =
    1 + Math.round((date.getTime() - firstThursday.getTime()) / (7 * 86400000));
  return { year: date.getUTCFullYear(), week };
}

/** Returns the [start, end] ISO date strings for a given ISO week. */
export function isoWeekBounds(year: number, week: number): { start: string; end: string } {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = (jan4.getUTCDay() + 6) % 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Day + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { start: toIso(monday), end: toIso(sunday) };
}

function toIso(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

// ── Calendar aggregation ─────────────────────────────────────

interface CalCellData {
  key: string;
  label: string;
  projects: number;
  forecast: number;
  brands: number;
}

/**
 * Build the grid data for the current selection level. Each cell exposes
 * a key (used to drill into the next level) plus the 3 counters.
 */
export function buildCalendarCells(
  projects: NormalizedProject[],
  sel: CalSelection,
): CalCellData[] {
  // Filter the projects to the current "context" of the selection.
  const inContext = projects.filter((p) => {
    const ref = p.deliveryDate || p.ocdDate;
    if (!ref) return false;
    const [yy, mm] = ref.split("-").map(Number);
    if (yy !== sel.year) return false;
    if (sel.quarter && Math.floor((mm - 1) / 3) + 1 !== sel.quarter) return false;
    if (sel.month && mm !== sel.month) return false;
    if (sel.week) {
      const iw = isoWeekOf(ref);
      if (iw.year !== sel.year || iw.week !== sel.week) return false;
    }
    return true;
  });

  const buckets = new Map<string, NormalizedProject[]>();

  if (sel.level === "year") {
    for (const p of inContext) buckets.set(`Y${sel.year}`, [...(buckets.get(`Y${sel.year}`) || []), p]);
    return cellsFromBuckets(buckets, (k) => `Year ${k.slice(1)}`);
  }

  if (sel.level === "quarter") {
    for (const p of inContext) {
      const m = Number((p.deliveryDate || p.ocdDate).slice(5, 7));
      const q = Math.floor((m - 1) / 3) + 1;
      const key = `Q${q}`;
      buckets.set(key, [...(buckets.get(key) || []), p]);
    }
    // Always emit Q1..Q4 even if empty so the grid stays stable.
    return ["Q1", "Q2", "Q3", "Q4"].map((key) => buildCell(key, key, buckets.get(key) || []));
  }

  if (sel.level === "month") {
    const monthsOfQuarter = sel.quarter
      ? [(sel.quarter - 1) * 3 + 1, (sel.quarter - 1) * 3 + 2, (sel.quarter - 1) * 3 + 3]
      : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
    const monthLabels = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    for (const p of inContext) {
      const m = Number((p.deliveryDate || p.ocdDate).slice(5, 7));
      const key = `M${m}`;
      buckets.set(key, [...(buckets.get(key) || []), p]);
    }
    return monthsOfQuarter.map((m) =>
      buildCell(`M${m}`, monthLabels[m - 1], buckets.get(`M${m}`) || []),
    );
  }

  if (sel.level === "week") {
    if (!sel.month) return [];
    const lastDay = new Date(Date.UTC(sel.year, sel.month, 0)).getUTCDate();
    const seenWeeks = new Set<number>();
    for (let d = 1; d <= lastDay; d += 1) {
      const iso = `${sel.year}-${String(sel.month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const { week } = isoWeekOf(iso);
      seenWeeks.add(week);
    }
    for (const p of inContext) {
      const ref = p.deliveryDate || p.ocdDate;
      const { week } = isoWeekOf(ref);
      const key = `W${week}`;
      buckets.set(key, [...(buckets.get(key) || []), p]);
    }
    return [...seenWeeks]
      .sort((a, b) => a - b)
      .map((w) => buildCell(`W${w}`, `Week ${w}`, buckets.get(`W${w}`) || []));
  }

  if (sel.level === "day") {
    if (!sel.week) return [];
    const { start } = isoWeekBounds(sel.year, sel.week);
    const startDate = new Date(`${start}T00:00:00Z`);
    const days: CalCellData[] = [];
    for (let i = 0; i < 7; i += 1) {
      const dayDate = new Date(startDate);
      dayDate.setUTCDate(startDate.getUTCDate() + i);
      const iso = toIso(dayDate);
      const dayProjects = inContext.filter((p) => (p.deliveryDate || p.ocdDate) === iso);
      const dayLabel = dayDate.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        timeZone: "UTC",
      });
      days.push(buildCell(`D${iso}`, dayLabel, dayProjects));
    }
    return days;
  }

  return [];
}

function cellsFromBuckets(
  buckets: Map<string, NormalizedProject[]>,
  labelFn: (key: string) => string,
): CalCellData[] {
  return [...buckets.entries()].map(([key, list]) => buildCell(key, labelFn(key), list));
}

function buildCell(key: string, label: string, list: NormalizedProject[]): CalCellData {
  return {
    key,
    label,
    projects: list.length,
    forecast: list.reduce((sum, p) => sum + p.forecastTotal, 0),
    brands: new Set(list.map((p) => p.brand)).size,
  };
}

// ── Calendar render ─────────────────────────────────────────

function renderCalendarView(): void {
  const allProjects = buildProjects();

  // Populate type filter dropdown
  const typeSelect = document.getElementById("bk-cal-type-filter") as HTMLSelectElement | null;
  if (typeSelect) {
    const types = unique(allProjects.map((p) => p.type).filter(Boolean)).sort();
    const prev = _calTypeFilter;
    typeSelect.innerHTML = `<option value="">All types</option>` +
      types.map((t) => `<option value="${escapeHtml(t)}"${t === prev ? " selected" : ""}>${escapeHtml(t)}</option>`).join("");
  }

  // Apply type filter
  const projects = _calTypeFilter
    ? allProjects.filter((p) => p.type === _calTypeFilter)
    : allProjects;

  const cells = buildCalendarCells(projects, _calSelection);
  renderCalendarBreadcrumb();
  renderCalendarGrid(cells);
  renderCalendarTable(filterProjectsForCurrentSelection(projects));
}

function renderCalendarBreadcrumb(): void {
  const host = document.getElementById("bk-cal-breadcrumb");
  if (!host) return;
  const parts: Array<{ label: string; level: CalLevel }> = [
    { label: `Year ${_calSelection.year}`, level: "year" },
  ];
  if (_calSelection.quarter)
    parts.push({ label: `Q${_calSelection.quarter}`, level: "quarter" });
  if (_calSelection.month) {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    parts.push({ label: monthNames[_calSelection.month - 1], level: "month" });
  }
  if (_calSelection.week) parts.push({ label: `Week ${_calSelection.week}`, level: "week" });
  if (_calSelection.day) parts.push({ label: _calSelection.day, level: "day" });

  host.innerHTML = parts
    .map(
      (p, i) =>
        `<button class="bk-breadcrumb-item${i === parts.length - 1 ? " is-current" : ""}" data-bk-cal-level="${p.level}" type="button">${escapeHtml(p.label)}</button>`,
    )
    .join('<span class="bk-breadcrumb-sep">›</span>');
}

function renderCalendarGrid(cells: CalCellData[]): void {
  const host = document.getElementById("bk-cal-grid");
  if (!host) return;
  if (!cells.length) {
    renderEmptyState(host, {
      icon: "📅",
      message: "No data for this period",
      hint: "Try selecting a different date range or removing the type filter.",
    });
    return;
  }
  host.innerHTML = cells
    .map(
      (c) => `<button class="bk-cal-cell" data-bk-cal-key="${escapeHtml(c.key)}" type="button"
          title="${escapeHtml(c.label)} | ${c.projects} project(s) | ${formatNumber(c.forecast)} forecast quantity | ${c.brands} brand(s)">
        <div class="bk-cal-cell-label">${escapeHtml(c.label)}</div>
        <div class="bk-cal-cell-main">${formatNumber(c.projects)}</div>
        <div class="bk-cal-cell-sub">${formatNumber(c.forecast)} forecast • ${formatNumber(c.brands)} brands</div>
      </button>`,
    )
    .join("");
}

function filterProjectsForCurrentSelection(all: NormalizedProject[]): NormalizedProject[] {
  return all.filter((p) => {
    const ref = p.deliveryDate || p.ocdDate;
    if (!ref) return false;
    const [yy, mm] = ref.split("-").map(Number);
    if (yy !== _calSelection.year) return false;
    if (_calSelection.quarter && Math.floor((mm - 1) / 3) + 1 !== _calSelection.quarter) return false;
    if (_calSelection.month && mm !== _calSelection.month) return false;
    if (_calSelection.week) {
      const iw = isoWeekOf(ref);
      if (iw.year !== _calSelection.year || iw.week !== _calSelection.week) return false;
    }
    if (_calSelection.day && ref !== _calSelection.day) return false;
    return true;
  });
}

function renderCalendarTable(projects: NormalizedProject[]): void {
  const body = document.getElementById("bk-cal-table-body");
  const title = document.getElementById("bk-cal-table-title");
  if (!body || !title) return;
  title.textContent = `Projects (${projects.length})`;
  if (!projects.length) {
    body.innerHTML = `<tr><td colspan="7" style="text-align:center;color:var(--muted);padding:1rem">No projects match the current selection.</td></tr>`;
    return;
  }
  body.innerHTML = projects
    .slice(0, 200)
    .map(
      (p) => `<tr>
        <td>${escapeHtml(p.brand)}</td>
        <td>${escapeHtml(p.projectName)}</td>
        <td>${escapeHtml(p.type)}</td>
        <td>${escapeHtml(p.hub)}</td>
        <td>${escapeHtml(p.ocdDate || "—")}</td>
        <td>${escapeHtml(p.deliveryDate || "—")}${deadlineBadgeHtml(p.deliveryDate)}</td>
        <td>${escapeHtml(formatNumber(p.forecastTotal))}</td>
      </tr>`,
    )
    .join("");
}

function bindCalendarHandlers(): void {
  // Type filter
  const typeSelect = document.getElementById("bk-cal-type-filter") as HTMLSelectElement | null;
  typeSelect?.addEventListener("change", () => {
    _calTypeFilter = typeSelect.value;
    renderCalendarView();
  });

  document.getElementById("bk-export-pdf")?.addEventListener("click", () => {
    const projects = buildProjects();
    const filtered = _calTypeFilter ? projects.filter((p) => p.type === _calTypeFilter) : projects;
    exportCalendarPdf({
      title: `ECF Launch Calendar — ${_calSelection.year}`,
      rows: filtered.map((p) => ({
        brand: p.brand,
        project: p.projectName,
        type: p.type,
        hub: p.hub,
        ocdDate: p.ocdDate,
        deliveryDate: p.deliveryDate,
        forecast: p.forecastTotal,
      })),
    });
  });

  document.addEventListener("click", (event) => {
    const target = event.target as HTMLElement;

    // Drill DOWN
    const cellBtn = target.closest<HTMLElement>("[data-bk-cal-key]");
    if (cellBtn) {
      drillCalendar(cellBtn.dataset.bkCalKey || "");
      return;
    }

    // Drill UP via breadcrumb
    const crumbBtn = target.closest<HTMLElement>("[data-bk-cal-level]");
    if (crumbBtn) {
      jumpCalendarLevel(crumbBtn.dataset.bkCalLevel as CalLevel);
      return;
    }

    // Reset button
    if (target.id === "bk-cal-reset") {
      _calSelection = { level: "year", year: new Date().getUTCFullYear() };
      renderCalendarView();
    }
  });
}

function drillCalendar(key: string): void {
  const sel = { ..._calSelection };
  if (sel.level === "year" && key.startsWith("Y")) {
    sel.level = "quarter";
  } else if (sel.level === "quarter" && key.startsWith("Q")) {
    sel.quarter = Number(key.slice(1));
    sel.level = "month";
  } else if (sel.level === "month" && key.startsWith("M")) {
    sel.month = Number(key.slice(1));
    sel.level = "week";
  } else if (sel.level === "week" && key.startsWith("W")) {
    sel.week = Number(key.slice(1));
    sel.level = "day";
  } else if (sel.level === "day" && key.startsWith("D")) {
    sel.day = key.slice(1);
    // Stay at "day" level but update the day filter; the table will refresh.
  }
  _calSelection = sel;
  renderCalendarView();
}

function jumpCalendarLevel(level: CalLevel): void {
  const sel: CalSelection = { level, year: _calSelection.year };
  if (level === "quarter" || level === "month" || level === "week" || level === "day")
    sel.quarter = _calSelection.quarter;
  if (level === "month" || level === "week" || level === "day")
    sel.month = _calSelection.month;
  if (level === "week" || level === "day") sel.week = _calSelection.week;
  if (level === "day") sel.day = _calSelection.day;
  // Going UP wipes the deeper-level selectors
  if (level === "year") {
    sel.quarter = undefined;
    sel.month = undefined;
    sel.week = undefined;
    sel.day = undefined;
  } else if (level === "quarter") {
    sel.month = undefined;
    sel.week = undefined;
    sel.day = undefined;
  } else if (level === "month") {
    sel.week = undefined;
    sel.day = undefined;
  } else if (level === "week") {
    sel.day = undefined;
  }
  _calSelection = sel;
  renderCalendarView();
}

// ── Gantt render ─────────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  Launch: "#3b82f6",
  Reno: "#a855f7",
  Hero: "#f59e0b",
  Inno: "#10b981",
  Refresh: "#06b6d4",
  Collab: "#ec4899",
  PDP: "#8b5cf6",
  Eventing: "#ef4444",
  Other: "#64748b",
};

function colorForType(type: string): string {
  return TYPE_COLORS[type] || TYPE_COLORS.Other;
}

function colorForProject(p: NormalizedProject): string {
  const baseColor = colorForType(p.type);
  // Overdue: red tint
  if (p.deliveryDate) {
    const delivery = new Date(p.deliveryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (delivery < today && p.forecastTotal === 0) {
      return "#ef4444"; // red — overdue and not validated
    }
  }
  // Validated: use type color at full opacity
  if (p.forecastTotal > 0) return baseColor;
  // Pending: dimmed version
  return baseColor + "80"; // 50% opacity via hex alpha
}

function renderGanttView(): void {
  const projects = buildProjects().filter((p) => p.ocdDate || p.deliveryDate);
  populateGanttFilters(projects);

  const yearSel = document.getElementById("bk-gantt-year") as HTMLSelectElement | null;
  const divSel = document.getElementById("bk-gantt-division") as HTMLSelectElement | null;
  const brandSel = document.getElementById("bk-gantt-brand") as HTMLSelectElement | null;

  const year = Number(yearSel?.value || new Date().getUTCFullYear());
  const divFilter = divSel?.value || "";
  const brandFilter = brandSel?.value || "";

  const visible = projects.filter((p) => {
    const ref = p.deliveryDate || p.ocdDate;
    if (!ref || Number(ref.slice(0, 4)) !== year) return false;
    if (divFilter && p.division !== divFilter) return false;
    if (brandFilter && p.brand !== brandFilter) return false;
    return true;
  });

  renderGanttLegend(visible);
  renderGanttSvg(visible, year);
}

function populateGanttFilters(projects: NormalizedProject[]): void {
  const yearSel = document.getElementById("bk-gantt-year") as HTMLSelectElement | null;
  const divSel = document.getElementById("bk-gantt-division") as HTMLSelectElement | null;
  const brandSel = document.getElementById("bk-gantt-brand") as HTMLSelectElement | null;

  if (yearSel && !yearSel.options.length) {
    const yearStrings = unique(
      projects
        .map((p) => (p.deliveryDate || p.ocdDate).slice(0, 4))
        .filter((s) => /^\d{4}$/.test(s)),
    ).sort() as string[];
    if (!yearStrings.length) yearStrings.push(String(new Date().getUTCFullYear()));
    yearSel.innerHTML = yearStrings
      .map((y) => `<option value="${y}">${y}</option>`)
      .join("");
    yearSel.value = yearStrings[0];
    yearSel.addEventListener("change", renderGanttView);
  }
  if (divSel && divSel.options.length <= 1) {
    const divs = unique(projects.map((p) => p.division).filter(Boolean)).sort();
    divSel.innerHTML =
      `<option value="">All</option>` +
      divs.map((d: string) => `<option value="${escapeHtml(d)}">${escapeHtml(d)}</option>`).join("");
    divSel.addEventListener("change", renderGanttView);
  }
  if (brandSel && brandSel.options.length <= 1) {
    const brands = unique(projects.map((p) => p.brand).filter(Boolean)).sort();
    brandSel.innerHTML =
      `<option value="">All</option>` +
      brands.map((b: string) => `<option value="${escapeHtml(b)}">${escapeHtml(b)}</option>`).join("");
    brandSel.addEventListener("change", renderGanttView);
  }
}

function renderGanttLegend(projects: NormalizedProject[]): void {
  const host = document.getElementById("bk-gantt-legend");
  if (!host) return;
  const types = unique(projects.map((p) => p.type).filter(Boolean));
  const typeLegend = types
    .map(
      (t: string) =>
        `<span class="bk-gantt-legend-item"><span class="bk-gantt-legend-dot" style="background:${colorForType(t)}"></span>${escapeHtml(t)}</span>`,
    )
    .join("");
  const statusLegend = `
    <span class="bk-gantt-legend-sep">|</span>
    <span class="bk-gantt-legend-item"><span class="bk-gantt-legend-dot" style="background:#ef4444"></span>Overdue</span>
    <span class="bk-gantt-legend-item"><span class="bk-gantt-legend-dot" style="opacity:0.5;background:#64748b"></span>Pending</span>
  `;
  host.innerHTML = typeLegend + statusLegend;
}

function renderGanttSvg(projects: NormalizedProject[], year: number): void {
  const host = document.getElementById("bk-gantt-content");
  if (!host) return;
  if (!projects.length) {
    host.innerHTML = `<p class="message" style="padding:1rem">No projects with dates for ${year}.</p>`;
    return;
  }

  // Group: division → brand → projects
  const grouped = new Map<string, Map<string, NormalizedProject[]>>();
  for (const p of projects) {
    if (!grouped.has(p.division)) grouped.set(p.division, new Map());
    const brandMap = grouped.get(p.division)!;
    if (!brandMap.has(p.brand)) brandMap.set(p.brand, []);
    brandMap.get(p.brand)!.push(p);
  }

  // Layout constants
  const ROW_H = 22;
  const HEADER_H = 28;
  const LEFT_W = 220;
  const RIGHT_PAD = 16;
  const monthLabels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const yearStart = Date.UTC(year, 0, 1);
  const yearEnd = Date.UTC(year + 1, 0, 1);
  const yearMs = yearEnd - yearStart;

  // Compute total rows (1 group header per division + 1 row per project + 1 brand header per brand)
  let totalRows = 0;
  for (const brandMap of grouped.values()) {
    totalRows += 1; // division row
    for (const projects of brandMap.values()) {
      totalRows += 1; // brand row
      totalRows += projects.length;
    }
  }

  const width = Math.max(800, host.clientWidth || 800);
  const innerW = width - LEFT_W - RIGHT_PAD;
  const height = HEADER_H + totalRows * ROW_H + 12;

  const xForIso = (iso: string): number => {
    const t = Date.parse(`${iso}T00:00:00Z`);
    if (!Number.isFinite(t)) return LEFT_W;
    const clamped = Math.max(yearStart, Math.min(yearEnd, t));
    return LEFT_W + ((clamped - yearStart) / yearMs) * innerW;
  };

  // Build SVG
  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" class="bk-gantt-svg">`,
  );

  // Background
  parts.push(`<rect x="0" y="0" width="${width}" height="${height}" fill="transparent"/>`);

  // Month grid
  for (let m = 0; m < 12; m += 1) {
    const x = LEFT_W + (m / 12) * innerW;
    parts.push(
      `<line x1="${x.toFixed(1)}" y1="${HEADER_H}" x2="${x.toFixed(1)}" y2="${height}" stroke="rgba(148,163,184,0.15)" stroke-width="1"/>`,
    );
    parts.push(
      `<text x="${(x + 4).toFixed(1)}" y="18" fill="#94a3b8" font-size="10" font-family="IBM Plex Sans, sans-serif">${monthLabels[m]}</text>`,
    );
  }
  // Final right border
  parts.push(
    `<line x1="${(LEFT_W + innerW).toFixed(1)}" y1="${HEADER_H}" x2="${(LEFT_W + innerW).toFixed(1)}" y2="${height}" stroke="rgba(148,163,184,0.15)"/>`,
  );

  // Header bottom rule
  parts.push(
    `<line x1="0" y1="${HEADER_H}" x2="${width}" y2="${HEADER_H}" stroke="rgba(148,163,184,0.25)"/>`,
  );

  // Rows
  let y = HEADER_H;
  for (const [division, brandMap] of grouped) {
    // Division header
    parts.push(
      `<rect x="0" y="${y}" width="${width}" height="${ROW_H}" fill="rgba(59,130,246,0.08)"/>`,
    );
    parts.push(
      `<text x="8" y="${y + 15}" fill="#f1f5f9" font-size="11" font-weight="700" font-family="IBM Plex Sans, sans-serif">${escapeHtml(division)}</text>`,
    );
    y += ROW_H;

    for (const [brand, list] of brandMap) {
      // Brand header
      parts.push(
        `<text x="20" y="${y + 15}" fill="#cbd5e1" font-size="10" font-weight="600" font-family="IBM Plex Sans, sans-serif">${escapeHtml(brand)} (${list.length})</text>`,
      );
      y += ROW_H;

      for (const p of list) {
        // Project label
        const label = p.projectName.length > 28 ? p.projectName.slice(0, 27) + "…" : p.projectName;
        parts.push(
          `<text x="32" y="${y + 14}" fill="#94a3b8" font-size="10" font-family="IBM Plex Sans, sans-serif">${escapeHtml(label)}</text>`,
        );

        const start = p.ocdDate || p.deliveryDate;
        const end = p.deliveryDate || p.ocdDate;
        if (start && end) {
          let x1 = xForIso(start);
          let x2 = xForIso(end);
          if (x2 < x1) [x1, x2] = [x2, x1];
          const barW = Math.max(4, x2 - x1);
          parts.push(
            `<rect class="bk-gantt-bar" x="${x1.toFixed(1)}" y="${(y + 4).toFixed(1)}" width="${barW.toFixed(1)}" height="${ROW_H - 8}" rx="3" fill="${colorForProject(p)}" opacity="0.85"><title>${escapeHtml(p.projectName)} — ${p.type} — ${start} → ${end}</title></rect>`,
          );
        }
        y += ROW_H;
      }
    }
  }

  parts.push(`</svg>`);
  host.innerHTML = parts.join("");
}

function bindGanttHandlers(): void {
  // Filter listeners are attached lazily inside populateGanttFilters
  // (only once, on first render).
}

// ── Market Validation render ─────────────────────────────────

function renderValidationView(): void {
  const projects = buildValidationProjects();

  const accessRules = getAccessRules();
  const brandsAllowedByAccess = computeAllowedBrands(projects, accessRules);

  // Brand picker visibility: hide and auto-select when only one brand is allowed.
  const brandWrap = document.getElementById("bk-val-brand-picker-wrap");
  const brandSel = document.getElementById("bk-val-filter-brand") as HTMLSelectElement | null;
  if (brandWrap && brandSel) {
    if (brandsAllowedByAccess.length === 1) {
      brandWrap.classList.add("hidden");
      _valSelectedBrand = brandsAllowedByAccess[0];
      brandSel.value = _valSelectedBrand;
    } else {
      brandWrap.classList.remove("hidden");
      populateBrandPicker(brandSel, brandsAllowedByAccess, _valSelectedBrand);
      _valSelectedBrand = brandSel.value;
    }
  }

  const divFilter = (document.getElementById("bk-val-filter-division") as HTMLSelectElement)?.value || "";
  const hubFilter = (document.getElementById("bk-val-filter-hub") as HTMLSelectElement)?.value || "";
  const typeFilter = (document.getElementById("bk-val-filter-type") as HTMLSelectElement)?.value || "";
  const statusFilter = (document.getElementById("bk-val-filter-status") as HTMLSelectElement)?.value || "";
  const searchQuery =
    (document.getElementById("bk-val-search") as HTMLInputElement)?.value.trim().toLowerCase() || "";

  // Progress bar uses the full brand-allowed set (before per-field filters)
  const progressContainer = document.getElementById("bk-val-progress");
  if (progressContainer) {
    const scope = projects.filter((p) => brandsAllowedByAccess.includes(p.brand));
    const buckets = { pending: 0, partial: 0, validated: 0, overdue: 0 };
    for (const p of scope) buckets[projectStatus(p)] += 1;
    renderProgressBar(progressContainer, {
      validated: buckets.validated,
      pending: buckets.pending + buckets.partial,
      overdue: buckets.overdue,
      totalBrands: new Set(scope.map((p) => p.brand)).size,
      totalDivisions: new Set(scope.map((p) => p.division)).size,
    });
  }

  // Start empty when the admin hasn't picked a brand yet
  if (!_valSelectedBrand) {
    const tbody = document.getElementById("bk-val-tbody");
    if (tbody) {
      renderEmptyState(tbody, {
        icon: "🏷️",
        message: "Pick a brand to see its projects",
        hint: "Select a brand from the filter above to load its validation queue.",
      });
    }
    updateValidateSelectedBtn();
    return;
  }

  let filtered = projects.filter((p) => p.brand === _valSelectedBrand);
  populateValidationFilters(filtered);

  if (divFilter) filtered = filtered.filter((p) => p.division === divFilter);
  if (hubFilter) filtered = filtered.filter((p) => p.countries.includes(hubFilter));
  if (typeFilter) filtered = filtered.filter((p) => p.type === typeFilter);
  if (statusFilter) filtered = filtered.filter((p) => projectStatus(p) === statusFilter);
  if (searchQuery)
    filtered = filtered.filter((p) =>
      [p.projectName, p.brand, p.division, p.type, p.countries.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(searchQuery),
    );

  filtered.sort((a, b) => {
    const av = String((a as any)[_valSortKey] || "");
    const bv = String((b as any)[_valSortKey] || "");
    return _valSortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / _valPageSize));
  if (_valPage > totalPages) _valPage = totalPages;
  const pageStart = (_valPage - 1) * _valPageSize;
  const pageItems = filtered.slice(pageStart, pageStart + _valPageSize);

  const tbody = document.getElementById("bk-val-tbody");
  if (tbody) {
    if (!pageItems.length) {
      renderEmptyState(tbody, {
        icon: "📋",
        message: "No projects match your filters",
        hint: "Try adjusting the filters above.",
      });
    } else {
      tbody.innerHTML = pageItems.map((p) => renderValidationRow(p)).join("");
    }
  }

  renderValidationPagination(filtered.length, totalPages);
  updateValidateSelectedBtn();
}

function computeAllowedBrands(
  projects: ValidationProject[],
  rules: readonly { hub: string; divisions: string[] }[],
): string[] {
  if (!rules.length) {
    return [...new Set(projects.map((p) => p.brand))].sort();
  }
  const allowedHubs = new Set(rules.map((r) => r.hub));
  const brands = new Set<string>();
  for (const p of projects) {
    if (p.countries.some((c) => allowedHubs.has(c))) brands.add(p.brand);
  }
  return [...brands].sort();
}

function populateBrandPicker(sel: HTMLSelectElement, brands: string[], current: string): void {
  const opts = ['<option value="">Select a brand…</option>'];
  for (const b of brands) {
    opts.push(
      `<option value="${escapeHtml(b)}"${b === current ? " selected" : ""}>${escapeHtml(b)}</option>`,
    );
  }
  sel.innerHTML = opts.join("");
}

function projectStatus(p: ValidationProject): "validated" | "partial" | "pending" | "overdue" {
  const vc = p.validatedCountries.size;
  if (vc > 0 && vc < p.countries.length) return "partial";
  if (vc === p.countries.length && p.countries.length > 0) return "validated";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const overdue = [...p.deliveryByCountry.values()].some(
    (d) => d && new Date(d) < today,
  );
  return overdue ? "overdue" : "pending";
}

function renderValidationRow(p: ValidationProject): string {
  const status = projectStatus(p);
  const statusLabel = {
    pending: "Pending",
    partial: "Partial",
    validated: "Validated",
    overdue: "Overdue",
  }[status];
  const statusClass = {
    pending: "status-pending",
    partial: "status-partial",
    validated: "status-ok",
    overdue: "status-danger",
  }[status];

  const visible = p.countries.slice(0, 3);
  const extra = p.countries.length - visible.length;
  const pills = visible
    .map((c) => {
      const v = p.validatedCountries.has(c) ? " bk-val-country-pill-ok" : "";
      return `<span class="bk-val-country-pill${v}" title="${escapeHtml(c)}">${escapeHtml(c)}</span>`;
    })
    .join("");
  const pillsMore = extra > 0
    ? `<span class="bk-val-country-pill bk-val-country-pill-more" title="${escapeHtml(p.countries.join(", "))}">+${extra}</span>`
    : "";

  const deliveryStr = escapeHtml(p.parentDeliveryDate || "—");
  const deadline = deadlineBadgeHtml(p.parentDeliveryDate);

  const checked = _valSelectedIds.has(p.id) ? "checked" : "";
  const rowClickable = status !== "validated";
  const actions = rowClickable
    ? `<span class="val-action val-action-primary" data-val-validate="${escapeHtml(p.id)}">${status === "partial" ? "Continue" : "Validate"}</span>`
    : `<span class="val-action-muted">—</span>`;

  return `<tr>
    <td><input type="checkbox" data-val-check="${escapeHtml(p.id)}" ${checked} ${rowClickable ? "" : "disabled"} /></td>
    <td style="font-weight:600">${escapeHtml(p.projectName)}</td>
    <td style="color:#93c5fd">${escapeHtml(p.brand)}</td>
    <td>${escapeHtml(p.division)}</td>
    <td class="bk-val-countries-cell">${pills}${pillsMore}</td>
    <td>${escapeHtml(p.type)}</td>
    <td>${deliveryStr}${deadline}</td>
    <td>${formatNumber(p.totalVolume)}</td>
    <td><span class="status-badge ${statusClass}">${statusLabel}</span></td>
    <td>${actions}</td>
  </tr>`;
}

function populateValidationFilters(projects: ValidationProject[]): void {
  const fill = (id: string, values: string[]) => {
    const sel = document.getElementById(id) as HTMLSelectElement | null;
    if (!sel) return;
    const current = sel.value;
    const opts = ['<option value="">All</option>'];
    for (const v of [...new Set(values)].sort()) {
      if (v) opts.push(
        `<option value="${escapeHtml(v)}"${v === current ? " selected" : ""}>${escapeHtml(v)}</option>`,
      );
    }
    sel.innerHTML = opts.join("");
  };
  fill("bk-val-filter-division", projects.map((p) => p.division));
  fill(
    "bk-val-filter-hub",
    projects.flatMap((p) => p.countries),
  );
  fill("bk-val-filter-type", projects.map((p) => p.type));
}

function renderValidationPagination(total: number, totalPages: number): void {
  const container = document.getElementById("bk-val-pagination");
  if (!container || totalPages <= 1) { if (container) container.innerHTML = ""; return; }
  const pages = [];
  for (let i = 1; i <= totalPages; i++) {
    pages.push(`<button class="pagination-btn ${i === _valPage ? "is-active" : ""}" data-val-page="${i}" type="button">${i}</button>`);
  }
  container.innerHTML = `<span style="color:var(--muted);font-size:0.75rem">Showing ${(_valPage - 1) * _valPageSize + 1}-${Math.min(_valPage * _valPageSize, total)} of ${total}</span> ${pages.join("")}`;
}

function updateValidateSelectedBtn(): void {
  const btn = document.getElementById("bk-val-validate-btn") as HTMLButtonElement | null;
  if (!btn) return;
  const n = _valSelectedIds.size;
  btn.textContent = n <= 1
    ? `Validate selected (${n})`
    : `Open first of ${n} selected`;
  btn.disabled = n === 0;
}

function bindValidationHandlers(): void {
  const view = document.getElementById("booking-validation-view");
  if (!view) return;

  // Filter changes
  for (const id of [
    "bk-val-filter-division",
    "bk-val-filter-hub",
    "bk-val-filter-type",
    "bk-val-filter-status",
  ]) {
    document.getElementById(id)?.addEventListener("change", () => {
      _valPage = 1;
      renderValidationView();
    });
  }
  document.getElementById("bk-val-filter-brand")?.addEventListener("change", (e) => {
    _valSelectedBrand = (e.target as HTMLSelectElement).value;
    _valPage = 1;
    _valSelectedIds.clear();
    renderValidationView();
  });
  document.getElementById("bk-val-search")?.addEventListener("input", () => { _valPage = 1; renderValidationView(); });

  // Click delegation
  view.addEventListener("click", async (e) => {
    const target = e.target as HTMLElement;

    // Sort column headers
    const sortTh = target.closest<HTMLElement>("[data-sort]");
    if (sortTh) {
      const key = (sortTh.dataset.sort || "projectName") as typeof _valSortKey;
      if (_valSortKey === key) _valSortAsc = !_valSortAsc;
      else { _valSortKey = key; _valSortAsc = true; }
      renderValidationView();
      return;
    }

    // Pagination
    const pageBtn = target.closest<HTMLElement>("[data-val-page]");
    if (pageBtn) {
      _valPage = Number(pageBtn.dataset.valPage) || 1;
      renderValidationView();
      return;
    }

    const validateBtn = target.closest<HTMLElement>("[data-val-validate]");
    if (validateBtn) {
      const projectId = validateBtn.dataset.valValidate || "";
      const projects = buildValidationProjects();
      const project = projects.find((p) => p.id === projectId);
      if (!project) return;
      const allowed = filterAllowedCountries(getAccessRules(), project.countries);
      if (allowed.length === 0) {
        showToast("You have no access to any country for this project.", "info");
        return;
      }
      const countries: ValidationCountry[] = allowed.map((code) => ({
        code,
        volume: project.volumeByCountry.get(code) || 0,
        defaultDate:
          project.deliveryByCountry.get(code) ||
          defaultChildDate(project.parentDeliveryDate),
        childIds: (project.childrenByCountry.get(code) || []).map((c: any) => String(c.id)),
      }));
      openValidationDrawer({
        projectName: project.projectName,
        brand: project.brand,
        division: project.division,
        type: project.type,
        parentDeliveryDate: project.parentDeliveryDate,
        assetCount: project.assetCount,
        totalVolume: project.totalVolume,
        countries,
        onValidate: async ({ entries, owner, notes }) => {
          const provider = getProvider();
          for (const entry of entries) {
            for (const childId of entry.childIds) {
              const child = project.childrenByCountry
                .get(entry.country)
                ?.find((c: any) => String(c.id) === childId);
              const quantity = Number((child as any)?.assetVolume || 0);
              await provider.upsertForecast({
                assetId: childId,
                date: entry.date,
                quantity,
                owner,
                notes,
                country: entry.country,
                brand: project.brand,
                division: project.division,
              } as any);
            }
          }
          showToast(
            `Forecast validated for ${entries.length} ${entries.length === 1 ? "country" : "countries"}.`,
            "success",
          );
          await triggerRefresh();
          renderValidationView();
          closeValidationDrawer();
        },
        onClose: () => {},
      });
      return;
    }
  });

  // Checkbox changes
  view.addEventListener("change", (e) => {
    const target = e.target as HTMLInputElement;

    // Individual checkbox
    const checkId = target.dataset.valCheck;
    if (checkId) {
      if (target.checked) _valSelectedIds.add(checkId);
      else _valSelectedIds.delete(checkId);
      updateValidateSelectedBtn();
      return;
    }

    // Select all
    if (target.id === "bk-val-select-all") {
      const checkboxes = view.querySelectorAll<HTMLInputElement>("[data-val-check]:not(:disabled)");
      for (const cb of checkboxes) {
        cb.checked = target.checked;
        if (target.checked) _valSelectedIds.add(cb.dataset.valCheck || "");
        else _valSelectedIds.delete(cb.dataset.valCheck || "");
      }
      updateValidateSelectedBtn();
    }
  });

  // Bulk validate button
  document.getElementById("bk-val-validate-btn")?.addEventListener("click", async () => {
    if (_valSelectedIds.size === 0) return;
    // Open drawer for first selected, then iterate
    showToast("Opening first selected project — batch mode is not yet supported.", "info");
    const projects = buildValidationProjects();
    const first = projects.find((p) => _valSelectedIds.has(p.id) && projectStatus(p) !== "validated");
    if (!first) return;
    // Reuse single validate flow for first project
    const validateBtn = document.querySelector(`[data-val-validate="${first.id}"]`) as HTMLElement | null;
    validateBtn?.click();
  });

  // CSV export
  document.getElementById("bk-val-export-csv")?.addEventListener("click", () => {
    const projects = buildValidationProjects();
    const headers = ["Project", "Brand", "Division", "Countries", "Type", "Delivery", "Volume", "Status"];
    const rows = projects.map((p) => [
      p.projectName,
      p.brand,
      p.division,
      p.countries.join("; "),
      p.type,
      p.parentDeliveryDate || "",
      String(p.totalVolume),
      projectStatus(p),
    ]);
    exportCsv(headers, rows, `ecf-validation-${new Date().toISOString().slice(0, 10)}.csv`);
  });
}

// ── Public re-exports kept for backward compat ───────────────

export function renderBookingPanel(): void {
  // Legacy export — redirects to the calendar view render.
  renderCalendarView();
}

export function setExternalRenderers(_renderers: any): void {
  // No-op kept so main.ts and other modules that may still call it
  // do not crash. EF-5 removed cross-panel renderer wiring.
}
